import React, { useState, useEffect, useCallback } from 'react';
import { Smartphone, Plus, RefreshCw, Trash2, Wifi, WifiOff, QrCode, Loader2 } from 'lucide-react';
import axios from './wabotAxios';
import { toast } from 'sonner';

import { WABOT_API } from './wabotApi';
const API = WABOT_API;

const WaBotDevices = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [qrModal, setQrModal] = useState(null); // { id, name, qrImage, status }

  const fetchSessions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/wabot/sessions`);
      // Defensive: only replace state if the response actually has data.
      // Prevents a freak empty response (e.g., gateway in middle of restart)
      // from briefly flashing "0 devices" on screen.
      const newSessions = r.data?.sessions;
      if (Array.isArray(newSessions)) {
        setSessions(newSessions);
      }
    } catch (e) { /* gateway may be down - keep last good data on screen */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSessions();
    const t = setInterval(fetchSessions, 8000);
    return () => clearInterval(t);
  }, [fetchSessions]);

  // Poll QR specifically when modal open
  useEffect(() => {
    if (!qrModal) return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/wabot/sessions/${qrModal.id}`);
        if (r.data.status === 'connected') {
          toast.success(`המכשיר "${qrModal.name}" מחובר! 📱`);
          setQrModal(null);
          fetchSessions();
        } else {
          setQrModal(prev => prev ? { ...prev, qrImage: r.data.qrImage, status: r.data.status } : null);
        }
      } catch (e) {}
    }, 2000);
    return () => clearInterval(t);
  }, [qrModal, fetchSessions]);

  const addDevice = async () => {
    if (!newName.trim()) { toast.error('שם מכשיר נדרש'); return; }
    setAdding(true);
    try {
      const r = await axios.post(`${API}/wabot/sessions`, { name: newName.trim() });
      toast.success('מכשיר נוצר - סרוק QR');
      setNewName('');
      // Open QR modal
      const sid = r.data.id;
      setQrModal({ id: sid, name: newName.trim(), qrImage: null, status: 'connecting' });
      fetchSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה ביצירת מכשיר');
    }
    setAdding(false);
  };

  const showQr = async (s) => {
    try {
      const r = await axios.get(`${API}/wabot/sessions/${s.id}`);
      setQrModal({ id: s.id, name: s.name, qrImage: r.data.qrImage, status: r.data.status });
    } catch (e) { toast.error('לא ניתן לטעון QR'); }
  };

  const restart = async (s) => {
    if (!window.confirm(`לאפס QR למכשיר "${s.name}"? תצטרך לסרוק שוב.`)) return;
    try {
      await axios.post(`${API}/wabot/sessions/${s.id}/restart`);
      toast.success('מאפס... QR חדש בדרך');
      setTimeout(() => showQr(s), 2000);
    } catch (e) { toast.error('שגיאה'); }
  };

  const removeDevice = async (s) => {
    if (!window.confirm(`למחוק את המכשיר "${s.name}"?`)) return;
    try {
      await axios.delete(`${API}/wabot/sessions/${s.id}`);
      toast.success('מכשיר נמחק');
      fetchSessions();
    } catch (e) { toast.error('שגיאה'); }
  };

  const statusBadge = (status) => {
    const map = {
      connected: { color: 'bg-green-100 text-green-700 border-green-200', label: 'מחובר', icon: Wifi },
      connecting: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'מתחבר...', icon: Loader2 },
      qr: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'סרוק QR', icon: QrCode },
      reconnecting: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'מתחבר מחדש', icon: RefreshCw },
      logged_out: { color: 'bg-stone-100 text-stone-500 border-stone-200', label: 'לא מחובר', icon: WifiOff },
    };
    const s = map[status] || { color: 'bg-stone-100 text-stone-500 border-stone-200', label: status, icon: WifiOff };
    const Icon = s.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${s.color}`}>
        <Icon className={`w-3 h-3 ${status === 'connecting' || status === 'reconnecting' ? 'animate-spin' : ''}`} />
        {s.label}
      </span>
    );
  };

  if (loading) return <div className="p-8 text-center text-stone-400" data-testid="wabot-devices-loading">טוען...</div>;

  return (
    <div className="p-6" dir="rtl" data-testid="wabot-devices">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-black text-[#2C3E2D]">המכשירים שלי</h2>
          <p className="text-sm text-stone-500 mt-1">חיבור מספרי וואטסאפ לשליחה אוטומטית</p>
        </div>
        <div className="flex items-center gap-3 bg-white rounded-xl p-2 border border-stone-200 shadow-sm">
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="שם המכשיר (למשל: ברי 1)"
            className="px-3 py-2 text-sm border-0 outline-none w-56"
            data-testid="wabot-new-device-name"
            onKeyDown={e => e.key === 'Enter' && addDevice()}
          />
          <button
            onClick={addDevice}
            disabled={adding}
            className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-50"
            data-testid="wabot-add-device-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            הוסף מכשיר
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="סה״כ מכשירים" value={sessions.length} color="bg-blue-50 text-blue-600" Icon={Smartphone} />
        <StatCard label="מחוברים" value={sessions.filter(s => s.status === 'connected').length} color="bg-green-50 text-green-600" Icon={Wifi} />
        <StatCard label="מנותקים" value={sessions.filter(s => s.status !== 'connected').length} color="bg-stone-50 text-stone-500" Icon={WifiOff} />
      </div>

      {/* Devices Grid */}
      {sessions.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border-2 border-dashed border-stone-200">
          <Smartphone className="w-16 h-16 mx-auto text-stone-300 mb-3" />
          <p className="text-stone-500 font-bold">אין עדיין מכשירים</p>
          <p className="text-stone-400 text-sm mt-1">לחץ על "הוסף מכשיר" כדי להתחבר למספר וואטסאפ</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map(s => (
            <div key={s.id} className="bg-white rounded-2xl p-5 border border-stone-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`wabot-device-${s.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-green-600" />
                </div>
                {statusBadge(s.status)}
              </div>
              <h3 className="font-black text-lg text-[#2C3E2D]">{s.name}</h3>
              {s.phone ? (
                <p className="text-sm text-stone-500 mt-1" dir="ltr">+{s.phone}</p>
              ) : (
                <p className="text-sm text-stone-400 mt-1">לא משויך מספר</p>
              )}
              <div className="flex gap-2 mt-4 pt-4 border-t border-stone-100">
                {s.status !== 'connected' && (
                  <button onClick={() => showQr(s)} className="flex-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg py-2 text-xs font-bold flex items-center justify-center gap-1" data-testid={`wabot-show-qr-${s.id}`}>
                    <QrCode className="w-3.5 h-3.5" /> הצג QR
                  </button>
                )}
                <button onClick={() => restart(s)} className="flex-1 bg-amber-50 text-amber-600 hover:bg-amber-100 rounded-lg py-2 text-xs font-bold flex items-center justify-center gap-1" data-testid={`wabot-restart-${s.id}`}>
                  <RefreshCw className="w-3.5 h-3.5" /> אפס
                </button>
                <button onClick={() => removeDevice(s)} className="bg-red-50 text-red-600 hover:bg-red-100 rounded-lg p-2 text-xs font-bold" data-testid={`wabot-delete-${s.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QR Modal */}
      {qrModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setQrModal(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full text-center" onClick={e => e.stopPropagation()} dir="rtl" data-testid="wabot-qr-modal">
            <h3 className="font-black text-xl text-[#2C3E2D] mb-2">סרוק QR</h3>
            <p className="text-sm text-stone-500 mb-4">פתח וואטסאפ ←⋮ ← מכשירים מקושרים ← קישור מכשיר</p>
            <div className="bg-stone-50 rounded-xl p-6 mb-4 min-h-[280px] flex items-center justify-center">
              {qrModal.qrImage ? (
                <img src={qrModal.qrImage} alt="QR Code" className="w-64 h-64" data-testid="wabot-qr-image" />
              ) : (
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin text-[#D97706] mx-auto mb-2" />
                  <p className="text-stone-500 text-sm">מייצר QR...</p>
                </div>
              )}
            </div>
            <button onClick={() => setQrModal(null)} className="w-full bg-stone-100 hover:bg-stone-200 rounded-lg py-3 font-bold text-stone-700" data-testid="wabot-qr-close">
              סגור
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color, Icon }) => (
  <div className="bg-white rounded-xl p-4 border border-stone-200 flex items-center gap-3">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <p className="text-xs text-stone-400">{label}</p>
      <p className="text-2xl font-black text-[#2C3E2D]">{value}</p>
    </div>
  </div>
);

export default WaBotDevices;
